import { Button } from "@common/index";
import styles from "./styles.module.scss";
import { useGetUserInfo } from "@hooks/useGetUserProfile";
import { Loader } from "@common/loader";
import { getPredictionData } from "./utils/getPredictionData";
import { useNavigate } from "react-router";
import {
  ChartGainWeight,
  ChartLoseWeight,
  ChartMaintainWeight,
} from "./charts";
import { formatDate } from "@utils/formatDate";
import { trackUserEvent } from "@utils/updateDataLayer";
import { getFunnelType } from "src/appConfig";

const title = "The only program you'll ever need to get your dream body";
export const Prediction = ({ onNextStep }) => {
  const isSomatic = getFunnelType() === "SOMATIC";
  const isWalky = getFunnelType().toLowerCase().includes("walky");

  const navigate = useNavigate();

  const handleNextStep = () => {
    onNextStep?.() ?? navigate("/confident");

    trackUserEvent("", {
      screenId: "occassion_result",
      screenTitle: title,
    });
  };
  const { user, loaded } = useGetUserInfo();

  const ms = localStorage.getItem("ms");
  const {
    goal,
    predictedDate,
    event,
    eventResponse,
    eventDate,
    eventWeight,
    isLoseWeight,
    isGainWeight,
    axisY,
    monthlyEstimateWeightChange,
    showYearInPredictedDate,
    eventDateXAxisOffset,
  } = getPredictionData(user);

  return (
    <>
      {loaded ? (
        <div className={styles.container} id="breakdown">
          <div className={styles.content}>
            <div className={styles.text}>
              <h2>{title}</h2>
              <p className={styles.subtitle}>We predict you'll be</p>
              <p
                className={
                  isWalky
                    ? `${styles.predictedData} ${styles.walky}`
                    : styles.predictedData
                }
              >
                {Math.ceil(eventWeight || goal)} {ms === "us" ? "lbs" : "kg"}{" "}
                <span>by</span> {formatDate(eventDate || predictedDate)}
              </p>
              {eventResponse && eventDate && (
                <div className={styles.event}>
                  {`~${monthlyEstimateWeightChange} ${
                    ms === "us" ? "lbs" : "kg"
                  }`}{" "}
                  <span>by the</span> {event?.displayValue}{" "}
                  {event?.displayValue === "Other" && "event"}{" "}
                  <div className={styles.icon}>{event?.icon}</div>
                </div>
              )}
            </div>

            <div
              className={
                loaded ? styles.chart : `${styles.chart} ${styles.hidden}`
              }
            >
              {eventResponse && eventDate && (
                <div className={styles.eventLabel}>
                  {event?.displayValue} - {formatDate(eventDate)}
                </div>
              )}
              <div className={styles.axisx}>
                <span>
                  {new Date().toLocaleString("en-US", {
                    month: "short",
                    day: "2-digit",
                  })}
                </span>
                <span>
                  {new Date(predictedDate).toLocaleString("en-US", {
                    month: "short",
                    day: "2-digit",
                    year: showYearInPredictedDate ? "numeric" : undefined,
                  })}
                </span>
              </div>
              <div className={styles.axisy}>
                {axisY.map((value, index) => {
                  if ((index === 0 || index === 2) && axisY[0] === axisY[2]) {
                    return <span key={index}></span>;
                  }
                  return <span key={index}>{value}</span>;
                })}
              </div>
              {eventDate && (
                <div
                  className={styles.eventDate}
                  style={{
                    left: `${eventDateXAxisOffset}%`,
                    transform: `translateX(-${eventDateXAxisOffset}%)`,
                  }}
                >
                  <span>
                    {new Date(eventDate).toLocaleString("en-US", {
                      month: "short",
                      day: "2-digit",
                    })}
                  </span>
                </div>
              )}
              <div
                className={
                  isLoseWeight
                    ? `${styles.label}`
                    : isGainWeight
                    ? `${styles.label} ${styles.gain}`
                    : isSomatic
                    ? `${styles.label} ${styles.maintain}`
                    : `${styles.label}`
                }
              >
                Goal <br />
                {goal} {ms === "us" ? "lbs" : "kg"}
              </div>

              {isLoseWeight ? (
                <ChartLoseWeight x={eventDateXAxisOffset} isWalky={isWalky} />
              ) : isGainWeight ? (
                <ChartGainWeight x={eventDateXAxisOffset} isWalky={isWalky} />
              ) : isSomatic ? (
                <ChartMaintainWeight
                  x={eventDateXAxisOffset}
                  isWalky={isWalky}
                />
              ) : (
                <ChartLoseWeight isWalky={isWalky} />
              )}
            </div>
            <div className={styles.hint}>
              *Established on the data provided by the users to the app. Check
              with your doctor first. This chart is just a visual representation
              of a possible fitness journey.
            </div>
          </div>

          <div className={styles.action}>
            <Button
              variant={isWalky ? "walky" : undefined}
              onClick={handleNextStep}
            >
              Continue
            </Button>
          </div>
        </div>
      ) : (
        <Loader isWalky={isWalky} />
      )}
    </>
  );
};
